import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineChartBar,
  HiOutlineCalendar,
  HiOutlineBuildingOffice2,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineUserGroup,
  HiOutlineTrophy,
} from "react-icons/hi2";
import { FaMedal, FaCrown, FaStar } from "react-icons/fa";
import { useGetAdminRankingOhiQuery } from "reduxFiles/api/reportAndFormApi";
import { useGetIndustriesQuery } from "reduxFiles/api/companyApi";
import { useTranslation } from "react-i18next";
import { useYearRangeDates } from "hooks/useYearRangeDates";
import { useScope } from "hooks/useScope";

interface AdminRankingOhiProps {
  className?: string;
}

interface FilterState {
  fromDate: string;
  toDate: string;
  industrialSectorId: number | null;
  mode: "top" | "bottom";
}

const AdminRankingOhi: React.FC<AdminRankingOhiProps> = ({
  className = "",
}) => {
  const { t } = useTranslation();
  const scope = useScope();

  const {
    defaultRange,
    years,
    getYearStartISO,
    getYearEndISO,
    extractYearFromISO,
  } = useYearRangeDates();

  const [filters, setFilters] = useState<FilterState>({
    fromDate: defaultRange.fromDate,
    toDate: defaultRange.toDate,
    industrialSectorId: null,
    mode: "top",
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      fromDate: defaultRange.fromDate,
      toDate: defaultRange.toDate,
    }));
  }, [defaultRange.fromDate, defaultRange.toDate]);

  const { data: rankingData, isLoading } = useGetAdminRankingOhiQuery({
    industrialSectorId: filters.industrialSectorId,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    mode: filters.mode,
    scope,
  });

  const handleYearChange = (year: number) => {
    setFilters((prev) => ({
      ...prev,
      fromDate: getYearStartISO(year),
      toDate: getYearEndISO(year),
    }));
  };

  const handleSectorChange = (sectorId: number | null) => {
    setFilters((prev) => ({ ...prev, industrialSectorId: sectorId }));
  };

  const handleModeChange = (mode: "top" | "bottom") => {
    setFilters((prev) => ({ ...prev, mode }));
  };

  const { data: sectors } = useGetIndustriesQuery();

  const getRankingIcon = (index: number, mode: "top" | "bottom") => {
    if (mode === "bottom") return null;

    switch (index) {
      case 0:
        return <FaCrown className="h-8 w-8 text-yellow-400" />;
      case 1:
        return <FaMedal className="h-8 w-8 text-gray-400" />;
      case 2:
        return <FaMedal className="h-8 w-8 text-amber-600" />;
      default:
        return <FaStar className="h-6 w-6 text-blue-400" />;
    }
  };

  const getRankingBgColor = (index: number, mode: "top" | "bottom") => {
    if (mode === "top") {
      if (index === 0)
        return "bg-gradient-to-br from-green-700/30 to-green-800/20 dark:from-green-600/40 dark:to-green-700/20 border-green-600/60 dark:border-green-500/60";
      if (index === 1)
        return "bg-gradient-to-br from-green-600/25 to-green-700/15 dark:from-green-500/35 dark:to-green-600/15 border-green-500/50 dark:border-green-400/50";
      if (index === 2)
        return "bg-gradient-to-br from-green-500/20 to-green-600/10 dark:from-green-400/30 dark:to-green-500/10 border-green-400/40 dark:border-green-300/40";
      if (index === 3)
        return "bg-gradient-to-br from-green-400/15 to-green-500/10 dark:from-green-300/25 dark:to-green-400/10 border-green-300/30 dark:border-green-200/30";
      return "bg-gradient-to-br from-green-300/10 to-green-400/5 dark:from-green-200/20 dark:to-green-300/5 border-green-200/20 dark:border-green-100/20";
    } else {
      if (index === 0)
        return "bg-gradient-to-br from-red-700/30 to-red-800/20 dark:from-red-600/40 dark:to-red-700/20 border-red-600/60 dark:border-red-500/60";
      if (index === 1)
        return "bg-gradient-to-br from-red-600/25 to-red-700/15 dark:from-red-500/35 dark:to-red-600/15 border-red-500/50 dark:border-red-400/50";
      if (index === 2)
        return "bg-gradient-to-br from-red-500/20 to-red-600/10 dark:from-red-400/30 dark:to-red-500/10 border-red-400/40 dark:border-red-300/40";
      if (index === 3)
        return "bg-gradient-to-br from-red-400/15 to-red-500/10 dark:from-red-300/25 dark:to-red-400/10 border-red-300/30 dark:border-red-200/30";
      return "bg-gradient-to-br from-red-300/10 to-red-400/5 dark:from-red-200/20 dark:to-red-300/5 border-red-200/20 dark:border-red-100/20";
    }
  };

  const getRankingBadgeColor = (index: number, mode: "top" | "bottom") => {
    if (mode === "top") {
      if (index === 0)
        return "bg-gradient-to-br from-green-700 to-green-800 shadow-green-700/40";
      if (index === 1)
        return "bg-gradient-to-br from-green-600 to-green-700 shadow-green-600/35";
      if (index === 2)
        return "bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/30";
      if (index === 3)
        return "bg-gradient-to-br from-green-400 to-green-500 shadow-green-400/25";
      return "bg-gradient-to-br from-green-300 to-green-400 shadow-green-300/20";
    } else {
      if (index === 0)
        return "bg-gradient-to-br from-red-700 to-red-800 shadow-red-700/40";
      if (index === 1)
        return "bg-gradient-to-br from-red-600 to-red-700 shadow-red-600/35";
      if (index === 2)
        return "bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30";
      if (index === 3)
        return "bg-gradient-to-br from-red-400 to-red-500 shadow-red-400/25";
      return "bg-gradient-to-br from-red-300 to-red-400 shadow-red-300/20";
    }
  };

  const getRankingTextColor = (index: number, mode: "top" | "bottom") => {
    if (mode === "top") {
      if (index === 0) return "text-green-800 dark:text-green-300";
      if (index === 1) return "text-green-700 dark:text-green-300";
      if (index === 2) return "text-green-600 dark:text-green-300";
      if (index === 3) return "text-green-500 dark:text-green-200";
      return "text-green-400 dark:text-green-200";
    } else {
      if (index === 0) return "text-red-800 dark:text-red-300";
      if (index === 1) return "text-red-700 dark:text-red-300";
      if (index === 2) return "text-red-600 dark:text-red-300";
      if (index === 3) return "text-red-500 dark:text-red-200";
      return "text-red-400 dark:text-red-200";
    }
  };

  const getRankingColor = (index: number, mode: "top" | "bottom") => {
    if (mode === "top") {
      if (index === 0) return "from-green-700 to-green-800";
      if (index === 1) return "from-green-600 to-green-700";
      if (index === 2) return "from-green-500 to-green-600";
      if (index === 3) return "from-green-400 to-green-500";
      return "from-green-300 to-green-400";
    } else {
      if (index === 0) return "from-red-700 to-red-800";
      if (index === 1) return "from-red-600 to-red-700";
      if (index === 2) return "from-red-500 to-red-600";
      if (index === 3) return "from-red-400 to-red-500";
      return "from-red-300 to-red-400";
    }
  };

  const displayCompanies = rankingData?.slice(0, 5) || [];

  const selectedYear = extractYearFromISO(filters.fromDate);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", damping: 20, stiffness: 300 }}
      className={`rounded-2xl bg-white shadow-xl dark:bg-gray-800/90 ${className}`}
    >
      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 p-2.5 text-white shadow-lg shadow-indigo-500/25">
              <HiOutlineChartBar className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {t("dashboard.rankingOhi.title")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t("dashboard.rankingOhi.description")}
              </p>
            </div>
          </div>
          {!isLoading && displayCompanies.length > 0 && (
            <div className="flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1.5 text-xs font-medium text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
              <HiOutlineTrophy className="h-4 w-4" />
              <span className="font-bold">{displayCompanies.length}</span>
              <span>{t("dashboard.rankingOhi.companies")}</span>
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">
              <HiOutlineCalendar className="me-1.5 inline-block h-4 w-4" />
              {t("dashboard.rankingOhi.year")}
            </label>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(Number(e.target.value))}
                className="w-full appearance-none rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:border-indigo-400 dark:focus:border-indigo-400"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 dark:text-gray-400">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">
              <HiOutlineBuildingOffice2 className="me-1.5 inline-block h-4 w-4" />
              {t("dashboard.rankingOhi.sector")}
            </label>
            <div className="relative">
              <select
                value={filters.industrialSectorId || ""}
                onChange={(e) =>
                  handleSectorChange(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
                className="w-full appearance-none rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm transition-all duration-200 hover:border-indigo-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:border-indigo-400 dark:focus:border-indigo-400"
              >
                <option value="" className="text-gray-500 dark:text-gray-400">
                  {t("dashboard.rankingOhi.allSectors")}
                </option>
                {sectors?.map((sector: any) => (
                  <option key={sector.id} value={sector.id}>
                    {t(`industrySectors.${sector.key}`)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 dark:text-gray-400">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">
              <HiOutlineUserGroup className="me-1.5 inline-block h-4 w-4" />
              {t("dashboard.rankingOhi.mode")}
            </label>
            <div className="flex gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-700/50">
              <button
                onClick={() => handleModeChange("top")}
                className={`relative flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                  filters.mode === "top"
                    ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg shadow-green-600/40"
                    : "text-gray-600 hover:bg-white/50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600/50 dark:hover:text-white"
                }`}
              >
                <HiOutlineArrowTrendingUp className="me-1.5 inline-block h-4 w-4" />
                {t("dashboard.rankingOhi.top")}
              </button>
              <button
                onClick={() => handleModeChange("bottom")}
                className={`relative flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-300 ${
                  filters.mode === "bottom"
                    ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-600/40"
                    : "text-gray-600 hover:bg-white/50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600/50 dark:hover:text-white"
                }`}
              >
                <HiOutlineArrowTrendingDown className="me-1.5 inline-block h-4 w-4" />
                {t("dashboard.rankingOhi.bottom")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="loader w-6 text-brand-500" />
          </div>
        ) : displayCompanies.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            <AnimatePresence mode="wait">
              {displayCompanies.map((company, index) => (
                <motion.div
                  key={company.companyId}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 250,
                    damping: 20,
                  }}
                  whileHover={{
                    y: -8,
                    scale: 1.05,
                    transition: { type: "spring", stiffness: 400 },
                  }}
                  className={`group relative overflow-hidden rounded-2xl border-2 p-4 transition-all duration-300 hover:shadow-2xl ${getRankingBgColor(
                    index,
                    filters.mode
                  )}`}
                >
                  <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-5 blur-2xl transition-opacity duration-300 group-hover:opacity-10">
                    <div
                      className={`h-full w-full bg-gradient-to-br ${getRankingColor(
                        index,
                        filters.mode
                      )}`}
                    />
                  </div>

                  <div className="relative flex flex-col items-center text-center">
                    <div className="relative mb-5">
                      <div
                        className={`flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-extrabold text-white shadow-lg transition-all duration-300 group-hover:rotate-3 group-hover:scale-110 ${getRankingBadgeColor(
                          index,
                          filters.mode
                        )}`}
                      >
                        #{index + 1}
                      </div>
                      {filters.mode === "top" && (
                        <div className="absolute -right-3 -top-3 transform transition-all duration-300 group-hover:rotate-12 group-hover:scale-125">
                          {getRankingIcon(index, filters.mode)}
                        </div>
                      )}
                    </div>

                    <h4 className="line-clamp-3 min-h-[2.5rem] px-1 text-sm font-bold text-gray-900 dark:text-white">
                      {company.companyName}
                    </h4>

                    <div className="mt-2 flex items-center justify-center gap-1.5">
                      <HiOutlineChartBar className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-400">
                        {t("dashboard.rankingOhi.ohiScore")} :
                      </span>
                      <span
                        className={`text-lg font-bold ${getRankingTextColor(
                          index,
                          filters.mode
                        )}`}
                      >
                        {company.ohi?.toFixed(2)} %
                      </span>
                    </div>

                    <div className="mt-3 w-full">
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{
                            width: `${Math.min(
                              (company.ohi / 100) * 100,
                              100
                            )}%`,
                          }}
                          transition={{
                            delay: 0.1 + index * 0.05,
                            duration: 0.8,
                            ease: "easeOut",
                          }}
                          className={`h-full rounded-full bg-gradient-to-r ${getRankingColor(
                            index,
                            filters.mode
                          )}`}
                        />
                      </div>
                    </div>

                    <div
                      className={`mt-3 h-0.5 w-12 rounded-full bg-gradient-to-r ${getRankingColor(
                        index,
                        filters.mode
                      )} opacity-30 transition-opacity duration-300 group-hover:opacity-60`}
                    />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-gray-100 p-6 dark:bg-gray-700">
              <HiOutlineChartBar className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h4 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              {t("dashboard.rankingOhi.noData")}
            </h4>
            <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.rankingOhi.noDataDescription")}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdminRankingOhi;
